/**
 * RequirementEvaluationCard - Requirement Evaluation Display Card
 * ============================================================================
 * DC-1044: Card component displaying a single requirement evaluation from
 * Tier 2 deep analysis
 *
 * Features:
 * - Status badge: Met (green), Partial (yellow), Not Met (red), N/A (gray)
 * - Requirement ID + title
 * - Score bar (0-100)
 * - Expandable section with evidence, gaps, remediation, confidence
 *
 * Pattern: Follows CredibilityGapsPanel.tsx (DC-933)
 *
 * @version 1.0.0
 * @created 2026-01-25
 * ============================================================================
 */

import React, { useState, useCallback } from 'react';
import {
  type RequirementEvaluation,
  type RegulationSchemaId,
  REGULATION_CONFIG,
} from './types';
import './RequirementEvaluationCard.css';

// ============================================================================
// Types
// ============================================================================

export interface RequirementEvaluationCardProps {
  /** The evaluation to display */
  evaluation: RequirementEvaluation;
  /** Regulation ID for context */
  regulationId: RegulationSchemaId;
  /** Whether card is expanded */
  expanded?: boolean;
  /** Toggle expanded state */
  onToggle?: () => void;
  /** Custom className */
  className?: string;
}

// ============================================================================
// Constants
// ============================================================================

const STATUS_CONFIG: Record<RequirementEvaluation['status'], {
  label: string;
  icon: string;
  color: string;
  bgColor: string;
}> = {
  met: {
    label: 'Met',
    icon: '✓',
    color: '#16A34A',
    bgColor: '#F0FDF4',
  },
  partial: {
    label: 'Partial',
    icon: '◐',
    color: '#CA8A04',
    bgColor: '#FEFCE8',
  },
  not_met: {
    label: 'Not Met',
    icon: '✗',
    color: '#DC2626',
    bgColor: '#FEF2F2',
  },
  not_applicable: {
    label: 'N/A',
    icon: '—',
    color: '#6B7280',
    bgColor: '#F3F4F6',
  },
};

// ============================================================================
// Helper Components
// ============================================================================

interface StatusBadgeProps {
  status: RequirementEvaluation['status'];
}

const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const config = STATUS_CONFIG[status];

  return (
    <span
      className="rec-status-badge"
      style={{
        backgroundColor: config.bgColor,
        color: config.color,
      }}
    >
      <span className="rec-status-badge__icon">{config.icon}</span>
      <span className="rec-status-badge__label">{config.label}</span>
    </span>
  );
};

interface ScoreBarProps {
  score: number;
}

const ScoreBar: React.FC<ScoreBarProps> = ({ score }) => {
  const getColor = (s: number): string => {
    if (s >= 90) return '#16A34A';
    if (s >= 70) return '#CA8A04';
    if (s >= 50) return '#EA580C';
    return '#DC2626';
  };

  return (
    <div className="rec-score">
      <div className="rec-score__bar-bg">
        <div
          className="rec-score__bar-fill"
          style={{
            width: `${score}%`,
            backgroundColor: getColor(score),
          }}
        />
      </div>
      <span className="rec-score__value">{Math.round(score)}</span>
    </div>
  );
};

interface ConfidenceIndicatorProps {
  confidence: number;
}

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({ confidence }) => {
  const getLevel = (c: number): { label: string; color: string } => {
    if (c >= 90) return { label: 'High', color: '#16A34A' };
    if (c >= 70) return { label: 'Medium', color: '#CA8A04' };
    return { label: 'Low', color: '#EA580C' };
  };

  const level = getLevel(confidence);

  return (
    <div className="rec-confidence">
      <span className="rec-confidence__label">AI Confidence:</span>
      <span className="rec-confidence__value" style={{ color: level.color }}>
        {level.label} ({Math.round(confidence)}%)
      </span>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const RequirementEvaluationCard: React.FC<RequirementEvaluationCardProps> = ({
  evaluation,
  regulationId,
  expanded: expandedProp,
  onToggle,
  className = '',
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const expanded = expandedProp !== undefined ? expandedProp : internalExpanded;
  const statusConfig = STATUS_CONFIG[evaluation.status];
  const regConfig = REGULATION_CONFIG[regulationId];

  const handleToggle = useCallback(() => {
    if (onToggle) {
      onToggle();
    } else {
      setInternalExpanded(!internalExpanded);
    }
  }, [onToggle, internalExpanded]);

  return (
    <div
      className={`rec-card rec-card--${evaluation.status} ${className}`}
      style={{ borderLeftColor: statusConfig.color }}
    >
      {/* Header (clickable) */}
      <button
        className="rec-card__header"
        onClick={handleToggle}
        aria-expanded={expanded}
      >
        <div className="rec-card__header-left">
          <StatusBadge status={evaluation.status} />
          <div className="rec-card__title-group">
            <span className="rec-card__req-id">
              {evaluation.section ? `${evaluation.section} ` : ''}
              {evaluation.requirement_id}
            </span>
            <span className="rec-card__title">{evaluation.requirement_title}</span>
          </div>
        </div>

        <div className="rec-card__header-right">
          <ScoreBar score={evaluation.score} />
          <svg
            className={`rec-card__chevron ${expanded ? 'rec-card__chevron--expanded' : ''}`}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Expanded Content */}
      {expanded && (
        <div className="rec-card__details">
          {/* Evidence */}
          {evaluation.evidence.length > 0 && (
            <div className="rec-card__section">
              <h5 className="rec-card__section-title">
                <span className="rec-card__section-icon">📋</span>
                Evidence Found ({evaluation.evidence.length})
              </h5>
              <ul className="rec-card__list">
                {evaluation.evidence.map((item, idx) => (
                  <li key={idx} className="rec-card__list-item rec-card__list-item--evidence">
                    <span className="rec-card__quote">&ldquo;{item}&rdquo;</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Gaps */}
          {evaluation.gaps.length > 0 && (
            <div className="rec-card__section">
              <h5 className="rec-card__section-title rec-card__section-title--gap">
                <span className="rec-card__section-icon">⚠</span>
                Gaps Identified ({evaluation.gaps.length})
              </h5>
              <ul className="rec-card__list">
                {evaluation.gaps.map((item, idx) => (
                  <li key={idx} className="rec-card__list-item rec-card__list-item--gap">
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Remediation */}
          {evaluation.remediation && (
            <div className="rec-card__section">
              <h5 className="rec-card__section-title rec-card__section-title--remediation">
                <span className="rec-card__section-icon">💡</span>
                Suggested Remediation
              </h5>
              <p className="rec-card__remediation">{evaluation.remediation}</p>
            </div>
          )}

          {/* Footer with confidence */}
          <div className="rec-card__footer">
            <ConfidenceIndicator confidence={evaluation.confidence} />
            <span className="rec-card__regulation">{regConfig.shortName}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default RequirementEvaluationCard;
